// src/components/sections/CandidateTestimonial.tsx
//
// Composant témoignage candidat (vraie scène : phone mockup + bulle quote).
// Vectoriel, responsive (stack mobile), animé framer-motion, branded.
//
// Usage minimal :
//   <CandidateTestimonial
//     name="Benoit"
//     role="Commercial"
//     verified
//     quote="Une **expérience candidat** vraiment bien pensée ! ..."
//   />
//
// Props complets — phoneTitle / phoneCompany / parcoursSlug pour
// personnaliser ce qui s'affiche dans le mockup téléphone.

import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';

interface Props {
  /** Prénom (ou prénom + nom) */
  name: string;
  /** Fonction / rôle court (ex: "Commercial", "Directrice Marketing") */
  role: string;
  /** Citation. Les segments entourés de **...** sont rendus en gras. */
  quote: string;
  /** Affiche le check vérifié à côté du nom */
  verified?: boolean;
  /** URL d'un avatar PNG/JPG. Sinon initiales sur gradient brand. */
  avatarUrl?: string;
  /** Titre du parcours affiché dans le mockup téléphone (ex: "Bienvenue chez Haschill") */
  phoneTitle?: string;
  /** Ligne secondaire dans le mockup ("HASCHILL · Cosmétique CBD") */
  phoneCompany?: string;
  /** Eyebrow optionnel au-dessus de la card */
  eyebrow?: string;
  /** Titre optionnel pour la section englobante (h2) */
  sectionTitle?: React.ReactNode;
  /** Variant fond : "light" (default, fbfaf8) ou "white" */
  background?: 'light' | 'white';
}

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

// Parse simple : remplace **gras** par <strong className="...">gras</strong>
function renderQuote(raw: string): React.ReactNode {
  const parts = raw.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return (
        <strong key={i} className="font-bold text-white">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function CandidateTestimonial({
  name,
  role,
  quote,
  verified = false,
  avatarUrl,
  phoneTitle = 'Bienvenue chez Haschill',
  phoneCompany = 'HASCHILL · Cosmétique CBD',
  eyebrow,
  sectionTitle,
  background = 'light',
}: Props) {
  const bgClass = background === 'light' ? 'bg-[#fbfaf8]' : 'bg-white';

  return (
    <section className={`${bgClass} py-16 md:py-20 lg:py-24`}>
      <div className="container-custom">
        {/* Header optionnel */}
        {(eyebrow || sectionTitle) && (
          <div className="max-w-4xl mx-auto text-center mb-12">
            {eyebrow && <span className={eyebrowClass}>{eyebrow}</span>}
            {sectionTitle && (
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]">
                {sectionTitle}
              </h2>
            )}
          </div>
        )}

        {/* Scène : phone à gauche, bubble à droite. Stack mobile.
            NOTE : pas de blobs gradient blur-3xl à l'intérieur de la motion.div
            — le navigateur rasterise le blur à chaque frame de l'animation
            opacity, ce qui créait un flash rose perçu comme une "ombre" au
            scroll. Les décorations qui restent (points jaunes + forme rouge
            derrière le phone) sont rendues sans blur, donc sans glitch. */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative max-w-5xl mx-auto"
        >
          {/* Petits points jaunes flottants (echo du témoignage source) */}
          <div
            aria-hidden
            className="absolute top-6 right-1/3 grid grid-cols-3 gap-2 opacity-60 pointer-events-none"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <span
                key={i}
                className="w-2 h-2 rounded-full bg-[#fde047]"
              />
            ))}
          </div>

          <div className="relative grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 lg:gap-10 items-center">
            {/* PHONE MOCKUP */}
            <div className="relative mx-auto lg:mx-0">
              {/* Forme rouge décorative derrière le phone */}
              <div
                aria-hidden
                className="absolute -inset-4 -z-10 rounded-[2.5rem] bg-gradient-to-br from-[#d03484] to-[#b81e7a] transform rotate-[-3deg] opacity-80"
              />

              <div className="relative w-[240px] mx-auto rounded-[2.5rem] bg-[#1f3245] p-2.5 shadow-2xl">
                {/* Encoche */}
                <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-20 h-5 bg-[#1f3245] rounded-b-2xl z-10" />

                <div className="rounded-[2rem] bg-white overflow-hidden aspect-[9/19] flex flex-col">
                  {/* Status bar fake */}
                  <div className="flex items-center justify-between px-5 pt-3 pb-2 text-[10px] font-semibold text-[#30475e]">
                    <span>9:41</span>
                    <span>●●●●●</span>
                  </div>

                  {/* App header */}
                  <div className="px-4 py-2 border-b border-neutral-100">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">
                      recruteurs.io
                    </p>
                    <p className="text-[10px] font-bold text-[#30475e] mt-0.5">
                      {phoneCompany}
                    </p>
                  </div>

                  {/* Content : faux parcours steps */}
                  <div className="flex-1 px-4 py-3 space-y-2 overflow-hidden">
                    <div className="rounded-xl ring-1 ring-neutral-200 p-2.5">
                      <p className="text-[9px] font-bold text-[#30475e] leading-tight mb-1">
                        {phoneTitle}
                      </p>
                      <p className="text-[8px] text-neutral-500 leading-snug">
                        Le premier responsable de secteur est recruté.
                      </p>
                    </div>

                    <div className="rounded-xl ring-1 ring-neutral-200 p-2.5">
                      <p className="text-[9px] font-bold text-[#30475e] leading-tight mb-1">
                        Prise de position sur le marché
                      </p>
                      <p className="text-[8px] text-neutral-500 leading-snug">
                        Développement de nouvelles gammes et lancement de l'enseigne.
                      </p>
                      <span className="inline-flex mt-1 text-[7px] font-bold uppercase tracking-widest text-[#d03484] bg-[#fde7f2] rounded-full px-1.5 py-0.5">
                        2023
                      </span>
                    </div>

                    <div className="rounded-xl ring-1 ring-neutral-200 p-2.5">
                      <p className="text-[9px] font-bold text-[#30475e] leading-tight mb-1">
                        Vos premières missions
                      </p>
                      <div className="h-1 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] mt-1.5 w-2/3" />
                    </div>
                  </div>

                  {/* Tab bar fake */}
                  <div className="flex items-center justify-around px-4 py-2 border-t border-neutral-100 bg-[#fbfaf8]">
                    <div className="w-6 h-1 rounded-full bg-[#d03484]" />
                    <div className="w-6 h-1 rounded-full bg-neutral-200" />
                    <div className="w-6 h-1 rounded-full bg-neutral-200" />
                  </div>
                </div>
              </div>
            </div>

            {/* CARD TÉMOIGNAGE */}
            <div className="relative">
              {/* Tail (flèche pointe vers phone) — visible desktop only */}
              <div
                aria-hidden
                className="hidden lg:block absolute top-12 -left-3 w-6 h-6 bg-gradient-to-br from-[#30475e] to-[#1f3245] transform rotate-45"
              />

              <div className="relative rounded-3xl bg-gradient-to-br from-[#30475e] to-[#1f3245] p-7 md:p-9 text-white shadow-2xl ring-1 ring-white/5">
                {/* Header card : avatar + nom + rôle */}
                <div className="flex items-center gap-4 mb-5">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={`${name} — ${role}`}
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-white/20"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-[#d03484]/30 ring-2 ring-white/20">
                      {getInitials(name)}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-lg font-bold text-white">{name}</span>
                      {verified && (
                        <span
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#3b82f6] flex-shrink-0"
                          title="Témoignage vérifié"
                          aria-label="Témoignage vérifié"
                        >
                          <CheckCircle2 className="w-3 h-3 text-white" />
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#fde7f2]/80 font-medium">{role}</p>
                  </div>

                  {/* Grosse guillemet décorative */}
                  <span
                    aria-hidden
                    className="absolute top-4 right-6 text-7xl font-serif text-white/10 leading-none select-none"
                  >
                    "
                  </span>
                </div>

                {/* Citation */}
                <blockquote className="text-base md:text-lg text-white/90 leading-relaxed">
                  {renderQuote(quote)}
                </blockquote>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
